import {
  Handshake,
  Megaphone,
  Calendar,
  Newspaper,
  ChartColumnIncreasing,
  FileHeartIcon,
  UserPlus,
} from 'lucide-react';
import { HandsPrayingIcon } from '@phosphor-icons/react';
import { BallotBoxIcon } from '@/components/icons/BallotBoxIcon';
import { toast } from 'sonner';
import { useUser } from '@/hooks/use-user';
import { useParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { hasCommunityPermission } from '@/lib/community-permissions';

interface CreatePostViewProps {
  onSelect: (type: string) => void;
}

interface TileProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

function Tile({ icon: Icon, label, onClick, disabled = false }: TileProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex flex-col items-center justify-center gap-2 rounded-2xl border border-border bg-white dark:bg-card p-4 shadow-sm transition-all aspect-square sm:aspect-auto',
        disabled
          ? 'cursor-not-allowed opacity-50'
          : 'cursor-pointer hover:border-primary/60 hover:shadow-md active:scale-95'
      )}
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-xl">
        <Icon className="h-8 w-8 text-primary" />
      </div>
      <span className="text-center text-[11px] font-semibold leading-tight text-foreground">
        {label}
      </span>
    </button>
  );
}

function LargeTile({ icon: Icon, label, onClick, disabled = false }: TileProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex flex-col items-center justify-center gap-2 rounded-2xl border border-border bg-white dark:bg-card p-4 shadow-sm transition-all aspect-square sm:aspect-auto',
        disabled
          ? 'cursor-not-allowed opacity-50'
          : 'cursor-pointer hover:border-primary/60 hover:shadow-md active:scale-95'
      )}
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-xl">
        <Icon className="h-12 w-12 text-primary" />
      </div>
      <span className="text-center text-[11px] font-semibold leading-tight text-foreground">
        {label}
      </span>
    </button>
  );
}

export function CreatePostView({ onSelect }: CreatePostViewProps) {
  const { communityId } = useParams<{ communityId: string }>();
  const { communities } = useUser();

  const currentCommunity = communities?.find((c) => c.id === communityId);
  const canCreateGeneralPost = hasCommunityPermission(
    currentCommunity?.permissions,
    'create_general_post'
  );

  const handleDeactivated = (type: string) => {
    toast.warning(`${type} posts are currently disabled.`);
  };

  const hasBusinessFeatures =
    currentCommunity?.isBusinessEnabled &&
    (currentCommunity?.isLeadEnabled ||
      currentCommunity?.isTestimonialEnabled ||
      currentCommunity?.isThankYouNoteEnabled ||
      currentCommunity?.isMeetupEnabled);

  return (
    <div className="space-y-6 pb-6 animate-in fade-in duration-500">
      {hasBusinessFeatures && (
        <div className="space-y-3">
          <h2 className="text-xl font-bold text-foreground">Business Post</h2>
          <div className="grid grid-cols-4 gap-3">
            {currentCommunity.isLeadEnabled && (
              <Tile
                icon={ChartColumnIncreasing}
                label="Requirement"
                onClick={() => onSelect('lead')}
              />
            )}
            {currentCommunity.isThankYouNoteEnabled && (
              <Tile
                icon={HandsPrayingIcon}
                label="Money Earned"
                onClick={() => onSelect('thank_you')}
              />
            )}
            {currentCommunity.isMeetupEnabled && (
              <Tile icon={Handshake} label="Meet Up" onClick={() => onSelect('meetup')} />
            )}
            {currentCommunity.isTestimonialEnabled && (
              <Tile
                icon={FileHeartIcon}
                label="Testimonials"
                onClick={() => onSelect('testimonial')}
              />
            )}
          </div>
        </div>
      )}

      <div className="space-y-3">
        <h2 className="text-xl font-bold text-foreground">Social Post</h2>
        <div className="grid grid-cols-4 gap-3">
          {canCreateGeneralPost && (
            <Tile icon={Newspaper} label="Gen Post" onClick={() => onSelect('general')} />
          )}
          <Tile icon={Calendar} label="Events" onClick={() => onSelect('event')} />
          {canCreateGeneralPost && (
            <LargeTile icon={BallotBoxIcon} label="Poll" onClick={() => onSelect('poll')} />
          )}
          <Tile
            icon={Megaphone}
            label="Marketing"
            onClick={() => handleDeactivated('Marketing')}
            disabled
          />
          <Tile
            icon={UserPlus}
            label="Invitee"
            onClick={() => handleDeactivated('Invitee')}
            disabled
          />
        </div>
      </div>
    </div>
  );
}
